import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { Post, PostCommentDto, PostFormValues } from '../models/post'

export default class BlogStore {
  postRegistry = new Map<string, Post>()
  selectedPost: Post | undefined = undefined
  postComments: PostCommentDto[] = []
  loadingComments = false
  loading = false
  loadingInitial = false

  constructor () {
    makeAutoObservable(this)
  }

  get postsByDate () {
    return Array.from(this.postRegistry.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  loadPosts = async (params?: { tagIds?: string[]; tagMatch?: string }) => {
    this.loadingInitial = true
    try {
      const result = await agent.Posts.list({
        tagIds: params?.tagIds,
        tagMatch: params?.tagMatch ?? 'any'
      })
      result.items.forEach((post) => this.setPost(post))
      runInAction(() => (this.loadingInitial = false))
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingInitial = false))
    }
  }

  loadPost = async (id: string) => {
    let post = this.postRegistry.get(id)
    if (post) {
      this.selectedPost = post
      return post
    }
    this.loadingInitial = true
    try {
      post = await agent.Posts.details(id)
      this.setPost(post)
      runInAction(() => {
        this.selectedPost = post
        this.loadingInitial = false
      })
      return post
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingInitial = false))
    }
  }

  private setPost = (post: Post) => {
    this.postRegistry.set(post.id, post)
  }

  updatePostVote = (postId: string, voteScore: number, currentUserVote: number | null) => {
    const existing = this.postRegistry.get(postId)
    if (!existing) return
    const updated: Post = { ...existing, voteScore, currentUserVote }
    this.postRegistry.set(postId, updated)
    if (this.selectedPost?.id === postId) {
      this.selectedPost = updated
    }
  }

  createPost = async (post: PostFormValues) => {
    this.loading = true
    try {
      const result = await agent.Posts.create(post)
      runInAction(() => {
        this.loading = false
      })
      return result?.id
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  updatePost = async (post: PostFormValues) => {
    this.loading = true
    try {
      await agent.Posts.update(post)
      runInAction(() => {
        if (post.id) {
          const updated = { ...this.postRegistry.get(post.id), ...post } as Post
          this.postRegistry.set(post.id, updated)
          this.selectedPost = updated
        }
        this.loading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  deletePost = async (id: string) => {
    this.loading = true
    try {
      await agent.Posts.delete(id)
      runInAction(() => {
        this.postRegistry.delete(id)
        this.selectedPost = undefined
        this.loading = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  clearSelectedPost = () => {
    this.selectedPost = undefined
    this.postComments = []
  }

  loadComments = async (postId: string) => {
    this.loadingComments = true
    try {
      const comments = await agent.Posts.listComments(postId)
      runInAction(() => {
        this.postComments = comments
        this.loadingComments = false
      })
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loadingComments = false))
    }
  }

  addComment = async (postId: string, body: string, parentCommentId?: number | null) => {
    try {
      const comment = await agent.Posts.addComment(postId, body, parentCommentId)
      runInAction(() => {
        this.postComments.push(comment)
      })
      return comment
    } catch (error) {
      console.error(error)
      throw error
    }
  }
}
